import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { useToastStore } from '@/store/toast'

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------
const TONE_STYLES = {
  success: 'border-l-success',
  error: 'border-l-danger',
  info: 'border-l-info',
} as const

export function Toaster() {
  const { toasts, dismiss } = useToastStore()

  return (
    <div
      className="pointer-events-none fixed inset-x-3 z-[60] flex flex-col gap-2 sm:inset-x-auto sm:right-5 sm:w-full sm:max-w-sm"
      // Above the phone tab bar; bottom-right on desktop.
      style={{ bottom: 'calc(var(--tabbar-h) + var(--safe-bottom) + 12px)' }}
      // `polite` so a toast never interrupts what the user is reading.
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'pointer-events-auto flex items-start gap-3 rounded-field border border-line border-l-[3px] bg-surface p-3.5 shadow-float animate-fade-up',
            TONE_STYLES[toast.tone],
          )}
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ink">{toast.title}</p>
            {toast.description && (
              <p className="mt-0.5 text-[13px] leading-relaxed text-ink-muted">
                {toast.description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => dismiss(toast.id)}
            aria-label="Dismiss"
            className="text-ink-faint transition-colors hover:text-ink"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty / loading / error states
// ---------------------------------------------------------------------------
export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string
  description?: string
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-10 text-center sm:py-14">
      {icon && <div className="mb-4 grid h-14 w-14 place-items-center rounded-2xl border border-brand/10 bg-brand-soft/60 text-brand">{icon}</div>}
      <h3 className="text-[17px] font-semibold text-ink">{title}</h3>
      {description && (
        <p className="mt-2 max-w-sm text-[14px] leading-relaxed text-ink-muted">{description}</p>
      )}
      {action && <div className="mt-5 flex w-full flex-col items-stretch gap-2 xs:w-auto xs:flex-row xs:flex-wrap xs:justify-center">{action}</div>}
    </div>
  )
}

export function ErrorState({
  title = 'Something went wrong',
  description,
  onRetry,
}: {
  title?: string
  description?: string
  onRetry?: () => void
}) {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-10 text-center sm:py-14">
      <div className="mb-3 grid h-11 w-11 place-items-center rounded-full bg-danger/10 text-danger">
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M10 6v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <circle cx="10" cy="14" r="1" fill="currentColor" />
          <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>
      <h3 className="text-[17px] font-semibold text-ink">{title}</h3>
      {description && <p className="mt-2 max-w-sm text-[14px] text-ink-muted">{description}</p>}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 rounded-field border border-line px-4 py-2 text-sm font-medium transition-colors hover:bg-surface-muted"
        >
          Try again
        </button>
      )}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton rounded-md', className)} />
}

export function PageSpinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3">
        <svg className="h-7 w-7 animate-spin text-ink-muted" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        <span className="text-sm text-ink-muted">{label}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------
const BADGE_TONES = {
  neutral: 'bg-surface-muted text-ink-soft',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/12 text-warning',
  danger: 'bg-danger/10 text-danger',
  info: 'bg-info/10 text-info',
  accent: 'bg-accent-soft text-ink',
} as const

export function Badge({
  children,
  tone = 'neutral',
  dot,
  className,
}: {
  children: ReactNode
  tone?: keyof typeof BADGE_TONES
  dot?: boolean
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[12px] font-medium',
        BADGE_TONES[tone],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  )
}
